"use client";

import { useLanguage } from "@/context/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";

type SoftiqIndexDialogProps = {
  isOpen: boolean;
  onClose: () => void;
};

export function SoftiqIndexDialog({ isOpen, onClose }: SoftiqIndexDialogProps) {
  const { language } = useLanguage();
  const copy = {
    cs: {
      title: "Co je SOFTIQ INDEX?",
      description:
        "SOFTIQ INDEX hodnotí nemovitost podle toho, jak moc je vhodná k investici.",
      scaleTitle: "Škála hodnocení",
      scaleDescription: "Index se pohybuje od -10 do +10:",
      negative: "Nevhodné hodnoty (-10 až -1)",
      negativeDescription:
        "Nemovitosti pro velmi náročné klienty. Nízký potenciál pro návratnost investice.",
      neutral: "Neutrální hodnoty (-1 až +1)",
      neutralDescription:
        "Nemovitosti s omezeným investičním potenciálem. Nepřinášejí dostatečnou návratnost investice a nejsou vhodné k rekonstrukci.",
      positive: "Vhodné hodnoty (+1 až +10)",
      positiveDescription:
        "Nemovitosti vhodné k rekonstrukci s velkým potenciálem pro vysokou návratnost investice. Čím vyšší číslo, tím větší potenciál.",
      howCalculated: "Jak se počítá?",
      howCalculatedDescription:
        "Index zohledňuje cenu za m², velikost nemovitosti a historii na základě vašeho hledání a nalezených nabídek.",
    },
    en: {
      title: "What is SOFTIQ INDEX?",
      description:
        "SOFTIQ INDEX evaluates properties based on their investment suitability.",
      scaleTitle: "Rating scale",
      scaleDescription: "The index ranges from -10 to +10:",
      negative: "Unsuitable values (-10 to -1)",
      negativeDescription:
        "Properties for very demanding clients. Low potential for return on investment.",
      neutral: "Neutral values (-1 to +1)",
      neutralDescription:
        "Properties with limited investment potential. Do not provide sufficient return on investment and are not suitable for reconstruction.",
      positive: "Suitable values (+1 to +10)",
      positiveDescription:
        "Properties suitable for reconstruction with great potential for high return on investment. The higher the number, the greater the potential.",
      howCalculated: "How is it calculated?",
      howCalculatedDescription:
        "The index takes into account price per m², property size, and historical data based on your search and found offers.",
    },
  }[language];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Visual Scale */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">
              {copy.scaleTitle}
            </h3>
            <p className="text-sm text-muted-foreground">
              {copy.scaleDescription}
            </p>

            {/* Scale visualization */}
            <div className="relative my-6 w-full">
              <div
                className="relative h-3 w-full rounded-full"
                style={{
                  background:
                    "linear-gradient(to right, #C65B5B 0%, #E3A17E 25%, #D7DEDA 50%, #8BB49F 75%, #4F8067 100%)",
                }}
              >
                {/* Zero marker */}
                <div className="absolute left-1/2 -top-1 h-5 w-0.5 bg-white" />
              </div>

              {/* Scale labels */}
              <div className="mt-2 flex w-full justify-between text-xs text-muted-foreground">
                <span>-10</span>
                <span>0</span>
                <span>+10</span>
              </div>
            </div>
          </div>

          {/* Explanation sections */}
          <div className="space-y-4">
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <h4 className="mb-1 text-sm font-semibold text-red-900">
                {copy.negative}
              </h4>
              <p className="text-xs text-red-800">{copy.negativeDescription}</p>
            </div>

            <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
              <h4 className="mb-1 text-sm font-semibold text-orange-900">
                {copy.neutral}
              </h4>
              <p className="text-xs text-orange-800">{copy.neutralDescription}</p>
            </div>

            <div className="rounded-lg border border-green-200 bg-green-50 p-3">
              <h4 className="mb-1 text-sm font-semibold text-green-900">
                {copy.positive}
              </h4>
              <p className="text-xs text-green-800">
                {copy.positiveDescription}
              </p>
            </div>
          </div>

          {/* How it's calculated */}
          <div className="rounded-lg border bg-muted/30 p-4">
            <h3 className="mb-2 text-sm font-semibold text-foreground">
              {copy.howCalculated}
            </h3>
            <p className="text-sm text-muted-foreground">
              {copy.howCalculatedDescription}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
